import { useCallback, useMemo } from 'react';
import { useConstructionTypes } from '../../../hooks/useConstructionTypes';
import { useGroups } from '../../../hooks/useGroups';
import { useLegalEntities } from '../../../hooks/useLegalEntities';
import { usePaymentMethods } from '../../../hooks/usePaymentMethods';
import { usePlans } from '../../../hooks/usePlans';
import { usePreAdjudicationReasons } from '../../../hooks/usePreAdjudicationReasons';
import { useReleaseReasons } from '../../../hooks/useReleaseReasons';
import { useUsers } from '../../../hooks/useUsers';

const normalizeId = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const createReferenceMap = (items, getLabel) => {
  const map = new Map();

  if (!Array.isArray(items)) return map;

  items.forEach((item) => {
    const id = normalizeId(item?.uuid);
    if (!id) return;

    const label = getLabel(item);
    if (!label) return;

    map.set(id, label);
  });

  return map;
};

const firstAvailable = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
};

export const useAuditReferenceData = () => {
  const { data: plans = [], isLoading: plansLoading } = usePlans();
  const { data: groups = [], isLoading: groupsLoading } = useGroups();
  const { data: paymentMethods = [], isLoading: paymentMethodsLoading } = usePaymentMethods();
  const { data: users = [], isLoading: usersLoading } = useUsers();
  const { data: legalEntities = [], isLoading: legalEntitiesLoading } = useLegalEntities();
  const { data: constructionTypes = [], isLoading: constructionTypesLoading } = useConstructionTypes();
  const { data: preAdjudicationReasons = [], isLoading: preAdjudicationReasonsLoading } =
    usePreAdjudicationReasons();
  const { data: releaseReasons = [], isLoading: releaseReasonsLoading } = useReleaseReasons();

  const referenceMaps = useMemo(
    () => ({
      uuid_plan: createReferenceMap(plans, (item) => firstAvailable(item?.name)),
      plan: createReferenceMap(plans, (item) => firstAvailable(item?.name)),
      uuid_group: createReferenceMap(groups, (item) => firstAvailable(item?.name)),
      group: createReferenceMap(groups, (item) => firstAvailable(item?.name)),
      uuid_payment_method: createReferenceMap(paymentMethods, (item) =>
        firstAvailable(item?.name, item?.code)
      ),
      payment_method: createReferenceMap(paymentMethods, (item) =>
        firstAvailable(item?.name, item?.code)
      ),
      uuid_user: createReferenceMap(users, (item) => firstAvailable(item?.names, item?.email)),
      uuid_user_gen: createReferenceMap(users, (item) => firstAvailable(item?.names, item?.email)),
      uuid_user_issued: createReferenceMap(users, (item) => firstAvailable(item?.names, item?.email)),
      uuid_user_che: createReferenceMap(users, (item) => firstAvailable(item?.names, item?.email)),
      uuid_user_can: createReferenceMap(users, (item) => firstAvailable(item?.names, item?.email)),
      user: createReferenceMap(users, (item) => firstAvailable(item?.names, item?.email)),
      uuid_legal_entity: createReferenceMap(legalEntities, (item) =>
        firstAvailable(item?.legal_business_name, item?.company)
      ),
      legal_entity: createReferenceMap(legalEntities, (item) =>
        firstAvailable(item?.legal_business_name, item?.company)
      ),
      uuid_construction_type: createReferenceMap(constructionTypes, (item) => firstAvailable(item?.name)),
      uuid_pre_adjudication_reason: createReferenceMap(preAdjudicationReasons, (item) =>
        firstAvailable(item?.name, item?.description)
      ),
      uuid_release_reason: createReferenceMap(releaseReasons, (item) =>
        firstAvailable(item?.description, item?.name)
      ),
      uuid_user_uuid: createReferenceMap(users, (item) => firstAvailable(item?.names, item?.email)),
    }),
    [
      constructionTypes,
      groups,
      legalEntities,
      paymentMethods,
      plans,
      preAdjudicationReasons,
      releaseReasons,
      users,
    ]
  );

  const resolveReference = useCallback(
    (fieldKey, rawValue) => {
      if (!fieldKey) return null;

      if (rawValue === null || rawValue === undefined || rawValue === '') return null;

      const normalizedFieldKey = String(fieldKey);

      if (normalizedFieldKey === 'uuid_group') {
        const groupValue = normalizeId(rawValue).toLowerCase();
        if (groupValue === 'all') return 'Todos';
      }

      const fieldMap = referenceMaps[normalizedFieldKey];
      if (!fieldMap) return null;

      const lookupKey = normalizeId(rawValue);
      if (!lookupKey) return null;

      return fieldMap.get(lookupKey) || null;
    },
    [referenceMaps]
  );

  return {
    resolveReference,
    isLoading:
      plansLoading ||
      groupsLoading ||
      paymentMethodsLoading ||
      usersLoading ||
      legalEntitiesLoading ||
      constructionTypesLoading ||
      preAdjudicationReasonsLoading ||
      releaseReasonsLoading,
  };
};

export default useAuditReferenceData;
